import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import emailService, { EmailCampaign } from '../../../services/emailService';
import { TrendingUp, TrendingDown, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface CampaignMetrics {
  campaignId: string;
  campaignName: string;
  sent: number;
  opened: number;
  replied: number;
  bounced: number;
  openRate: number;
  replyRate: number;
  bounceRate: number;
  avgResponseTime: number;
}

const CampaignComparison: React.FC = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<CampaignMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, [user]);

  const loadCampaigns = async () => {
    try {
      const data = await emailService.getCampaigns();
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    }
  };

  const handleCampaignToggle = (campaignId: string) => {
    setSelectedCampaigns(prev => {
      if (prev.includes(campaignId)) {
        return prev.filter(id => id !== campaignId);
      } else if (prev.length < 5) {
        return [...prev, campaignId];
      } else {
        toast.error('Maximum 5 campaigns for comparison');
        return prev;
      }
    });
  };

  const loadComparisonData = async () => {
    if (selectedCampaigns.length < 2) {
      toast.error('Select at least 2 campaigns to compare');
      return;
    }

    setIsLoading(true);
    try {
      const metricsPromises = selectedCampaigns.map(async (campaignId) => {
        const campaign = campaigns.find(c => c.id === campaignId);
        if (!campaign) return null;

        // Calculate metrics
        const sent = campaign.sent_count || 0;
        const opened = campaign.opened_count || 0;
        const replied = campaign.replied_count || 0;
        const bounced = campaign.bounced_count || 0;

        return {
          campaignId,
          campaignName: campaign.name,
          sent,
          opened,
          replied,
          bounced,
          openRate: sent > 0 ? (opened / sent) * 100 : 0,
          replyRate: sent > 0 ? (replied / sent) * 100 : 0,
          bounceRate: sent > 0 ? (bounced / sent) * 100 : 0,
          avgResponseTime: 24 // Placeholder
        };
      });

      const metrics = (await Promise.all(metricsPromises)).filter(Boolean) as CampaignMetrics[];
      setComparisonData(metrics);
    } catch (error) {
      console.error('Error loading comparison data:', error);
      toast.error('Failed to load comparison data');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (comparisonData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Campaign', 'Sent', 'Opened', 'Replied', 'Bounced', 'Open Rate %', 'Reply Rate %', 'Bounce Rate %'];
    const rows = comparisonData.map(d => [
      d.campaignName,
      d.sent,
      d.opened,
      d.replied,
      d.bounced,
      d.openRate.toFixed(2),
      d.replyRate.toFixed(2),
      d.bounceRate.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign-comparison-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success('Exported to CSV!');
  };

  // Prepare chart data
  const barChartData = comparisonData.map(d => ({
    name: d.campaignName.substring(0, 20),
    'Open Rate': d.openRate,
    'Reply Rate': d.replyRate,
    'Bounce Rate': d.bounceRate
  }));

  const radarChartData = comparisonData.map(d => ({
    campaign: d.campaignName.substring(0, 15),
    openRate: d.openRate,
    replyRate: d.replyRate * 10, // Scale up for visibility
    delivery: 100 - d.bounceRate,
    engagement: (d.openRate + d.replyRate * 5) / 2
  }));

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Campaign Comparison</h2>
          <p className="text-gray-600 mt-1">Compare up to 5 campaigns side-by-side</p>
        </div>
        {comparisonData.length > 0 && (
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        )}
      </div>

      {/* Campaign Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Campaigns (2-5)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              onClick={() => handleCampaignToggle(campaign.id!)}
              className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                selectedCampaigns.includes(campaign.id!)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {campaign.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {campaign.sent_count || 0} sent
                  </p>
                </div>
                {selectedCampaigns.includes(campaign.id!) && (
                  <div className="ml-2 flex-shrink-0">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={loadComparisonData}
          disabled={selectedCampaigns.length < 2 || isLoading}
          className="mt-4 w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Loading...' : 'Compare Selected Campaigns'}
        </button>
      </div>

      {/* Comparison Results */}
      {comparisonData.length > 0 && (
        <div className="space-y-6">
          {/* Summary Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Replied</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reply Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {comparisonData.map((campaign, index) => (
                    <tr key={campaign.campaignId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {campaign.campaignName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaign.sent.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaign.opened.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaign.replied.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`text-sm font-medium ${
                            campaign.openRate > 30 ? 'text-green-600' :
                            campaign.openRate > 15 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {campaign.openRate.toFixed(1)}%
                          </span>
                          {index > 0 && (
                            campaign.openRate > comparisonData[0].openRate ? (
                              <TrendingUp className="w-4 h-4 ml-2 text-green-600" />
                            ) : campaign.openRate < comparisonData[0].openRate ? (
                              <TrendingDown className="w-4 h-4 ml-2 text-red-600" />
                            ) : null
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`text-sm font-medium ${
                            campaign.replyRate > 5 ? 'text-green-600' :
                            campaign.replyRate > 2 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {campaign.replyRate.toFixed(1)}%
                          </span>
                          {index > 0 && (
                            campaign.replyRate > comparisonData[0].replyRate ? (
                              <TrendingUp className="w-4 h-4 ml-2 text-green-600" />
                            ) : campaign.replyRate < comparisonData[0].replyRate ? (
                              <TrendingDown className="w-4 h-4 ml-2 text-red-600" />
                            ) : null
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bar Chart */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Open Rate" fill="#3b82f6" />
                <Bar dataKey="Reply Rate" fill="#10b981" />
                <Bar dataKey="Bounce Rate" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Winner Analysis */}
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 Winner Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Best Open Rate</p>
                <p className="text-xl font-bold text-green-600">
                  {comparisonData.reduce((best, curr) => curr.openRate > best.openRate ? curr : best).campaignName}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {comparisonData.reduce((best, curr) => curr.openRate > best.openRate ? curr : best).openRate.toFixed(1)}%
                </p>
              </div>

              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Best Reply Rate</p>
                <p className="text-xl font-bold text-blue-600">
                  {comparisonData.reduce((best, curr) => curr.replyRate > best.replyRate ? curr : best).campaignName}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {comparisonData.reduce((best, curr) => curr.replyRate > best.replyRate ? curr : best).replyRate.toFixed(1)}%
                </p>
              </div>

              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Best Deliverability</p>
                <p className="text-xl font-bold text-purple-600">
                  {comparisonData.reduce((best, curr) => curr.bounceRate < best.bounceRate ? curr : best).campaignName}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {(100 - comparisonData.reduce((best, curr) => curr.bounceRate < best.bounceRate ? curr : best).bounceRate).toFixed(1)}% delivered
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignComparison;
